// mostly copied from https://github.com/mrdoob/three.js/blob/dev/examples/jsm/loaders/STLLoader.js
import ExternalLoader, {
  GeometryRawData,
  LoaderDataType,
  Material,
  ObjectData
} from "_src/engine/loaders/external/external_loader";
import config from "_src/config";
import {messageType} from "_src/engine/ipc";
import workerManager from "_src/engine/worker_manager";
import stlCommon, {Buffer} from "_src/engine/loaders/external/stl/stl_common";
import output from "_src/engine/output";
import {StreamProxy} from "_src/engine/loaders/external/utils";

const bufferSizeLimit = 1850000000 / 4; //1.8gb / 4bytes per float ot uint
export default class StlLoader extends ExternalLoader {
  constructor() {
    super();
    this._dataType = LoaderDataType.STREAM;

    this._headerTailDataChunk = null;
    this._isBinary = false;
    this._facesCount = null;
  }

  async _parse(reader, progress = null) {
    async function parseHeader() {
      const headerLength = 84;
      let dataChunk = await streamProxy.getChunk(false);
      if (!dataChunk) {
        throw new Error( "ExternalLoader.StlLoader: Stl file is empty");
      }

      while (dataChunk.byteLength < headerLength) {
        const nextChunk = await streamProxy.getChunk(false);
        if (!nextChunk) {
          throw new Error( "ExternalLoader.StlLoader: header is truncated");
        }

        const totalLength = dataChunk.byteLength + nextChunk.byteLength;
        const mergedChunk = new Uint8Array(totalLength);
        mergedChunk.set(dataChunk, 0);
        mergedChunk.set(nextChunk, dataChunk.byteLength);
        dataChunk = mergedChunk;
      }

      scope._isBinary = _isBinary(dataChunk);
      scope._facesCount = scope._isBinary ? (new DataView(dataChunk.buffer)).getUint32(80, true) : null;

      if (scope._isBinary) {
        scope._headerTailDataChunk = dataChunk.byteLength > headerLength ? dataChunk.subarray(headerLength) : null;
      } else {
        scope._headerTailDataChunk = dataChunk;
      }
    }

    async function parseBinary() {
      class FixedSizeBuffer extends Buffer {
        constructor(facesCount) {
          super();

          if (3 * 3 * facesCount > bufferSizeLimit) {
            throw new Error( "ExternalLoader.StlLoader: model is too big");
          }
          this.store[this.VERTICES] = new Float32Array(3 * 3 * facesCount);
          this.store[this.NORMALS] = new Float32Array(3 * 3 * facesCount);
          this.pointers = [0, 0];
        }

        push(bufferType, data) {
          if (ArrayBuffer.isView(data)) {
            this.store[bufferType].set(data, this.pointers[bufferType]);
            this.pointers[bufferType] += data.length;
          } else {
            this.store[bufferType][this.pointers[bufferType]++] = data;
          }
        }
      }
      const buffer = new FixedSizeBuffer(scope._facesCount);

      let currentChunk;
      let nextChunk;
      let dataView;
      let dataSize;

      const buildDataView = () => {
        let mergedChunk = null;
        if (currentChunk && nextChunk) {
          const totalLength = currentChunk.byteLength + nextChunk.byteLength;
          mergedChunk = new Uint8Array(totalLength);
          mergedChunk.set(currentChunk, 0);
          mergedChunk.set(nextChunk, currentChunk.byteLength);
        } else if (currentChunk) {
          mergedChunk = currentChunk;
        }
        dataView = mergedChunk ? new DataView(mergedChunk.buffer) : null;
        dataSize = currentChunk ? currentChunk.byteLength : 0;
      }
      const advanceChunks = async () => {
        [currentChunk, nextChunk] = await streamProxy.getTwoChunks();
      }

      await advanceChunks();
      if (scope._headerTailDataChunk) {
        if (currentChunk) {
          const mergedChunk = new Uint8Array(scope._headerTailDataChunk.length + currentChunk.length);
          mergedChunk.set(scope._headerTailDataChunk);
          mergedChunk.set(currentChunk, scope._headerTailDataChunk.byteLength);
          currentChunk = mergedChunk;
        } else {
          currentChunk = scope._headerTailDataChunk;
        }
      }
      buildDataView();

      if (!dataView) {
        throw new Error( "ExternalLoader.StlLoader: No data after header");
      }
      for (;;) {
        const parsePromise = parseChunk(dataView, dataSize, buffer);
        const loadPromise = advanceChunks();

        await Promise.all([loadPromise, parsePromise]);
        buildDataView();
        if (!dataView) break;
      }
      if (buffer.pointers[buffer.VERTICES] !== buffer.store[buffer.VERTICES].length) {
        output.warn( "ExternalLoader.StlLoader: File contains less faces than described in the header");
      }

      return postProcess( buffer );
    }

    async function parseASCII() {
      class ReallocatingBuffer extends Buffer {
        constructor() {
          super();

          this.store[this.VERTICES] = new Float32Array(1);
          this.store[this.NORMALS] = new Float32Array(1);
          this.pointers = [0, 0];
        }

        push(bufferType, data) {
          const length = ArrayBuffer.isView(data) ? data.length : 1;
          if (this.store[bufferType].length < this.pointers[bufferType] + length) {
            const oldBuffer = this.store[bufferType];
            const sizeRequest = this.pointers[bufferType] + length;
            if (sizeRequest > bufferSizeLimit) {
              throw new Error( "ExternalLoader.ObjLoader: model is too big");
            }
            const newSize = Math.min(bufferSizeLimit, sizeRequest * 2);
            this.store[bufferType] = new Float32Array(newSize);
            this.store[bufferType].set(oldBuffer);
          }

          if (ArrayBuffer.isView(data)) {
            this.store[bufferType].set(data, this.pointers[bufferType]);
            this.pointers[bufferType] += data.length;
          } else {
            this.store[bufferType][this.pointers[bufferType]++] = data;
          }
        }

        shrinkToFit() {
          this.store[this.VERTICES] = this.store[this.VERTICES].subarray(0, this.pointers[this.VERTICES]);
          this.store[this.NORMALS] = this.store[this.NORMALS].subarray(0, this.pointers[this.NORMALS]);
        }
      }
      const buffer = new ReallocatingBuffer();

      let dataChunk = scope._headerTailDataChunk ? scope._headerTailDataChunk : await streamProxy.getChunk();
      if (!dataChunk) {
        throw new Error( "ExternalLoader.StlLoader: No data after header");
      }

      for (;;) {
        const parsePromise = parseChunk(dataChunk, 0, buffer);
        const loadPromise = streamProxy.getChunk();

        dataChunk = (await Promise.all([loadPromise, parsePromise]))[0];
        if (!dataChunk) break;
      }

      buffer.shrinkToFit();

      return postProcess( buffer );
    }

    function postProcess( buffer ) {
      const geometryData = new GeometryRawData(buffer.store[buffer.VERTICES]);
      geometryData.normals = buffer.store[buffer.NORMALS];
      const materialData = new Material();
      const objectData = new ObjectData(geometryData, materialData);

      return objectData;
    }

    async function initParser() {
      config.useWorkers ? await initParserWorker() : await initParserMain();
    }

    async function initParserWorker() {
      const message = {
        type : messageType.INIT_STL,
        facesCount : scope._facesCount,
      };
      const tid = workerManager.generateUniqueID();
      await workerManager.sendMessage(message, [], tid);
    }

    async function initParserMain() {
      stlCommon.init(scope._facesCount);
    }

    async function parseChunk(dataChunk, dataSize, buffer) {
      config.useWorkers ? await parseChunkWorker(dataChunk, dataSize, buffer) : await parseChunkMain(dataChunk, dataSize, buffer);
    }

    async function parseChunkMain(dataChunk, dataSize, buffer) {
      scope._isBinary ? stlCommon.decodeBinary(dataChunk, dataSize, buffer) : stlCommon.decodeASCII(dataChunk, buffer);
    }

    async function parseChunkWorker(dataChunk, dataSize, buffer) {
      const message = {
        type : messageType.PARSE_STL,
        dataChunk : dataChunk,
        dataSize : dataSize,
        isBinary : scope._isBinary,
      };
      const tid = workerManager.generateUniqueID();
      const transfer = [dataChunk.buffer];

      const workerData = (await workerManager.sendMessage(message, transfer, tid)).data;
      buffer.push(buffer.VERTICES, workerData.vertices);
      buffer.push(buffer.NORMALS, workerData.normals);
    }

    const scope = this;
    const streamProxy = new StreamProxy(reader, progress);
    streamProxy.start();
    await parseHeader();
    await initParser();

    const objectData = scope._isBinary ? await parseBinary() : await parseASCII();

    if (progress) progress.emit(progress.LOADING, 1);
    return [objectData];
  }
}

function _isBinary( data ) {
  function matchDataViewAt( query, view, offset ) {
    // Check if each byte in query matches the corresponding byte from the current offset
    for ( let i = 0, il = query.length; i < il; i ++ ) {
      if ( query[ i ] !== view.getUint8( offset + i ) ) return false;
    }
    return true;
  }

  const view = new DataView(data.buffer);
  // An ASCII STL data must begin with 'solid ' as the first six bytes.
  // However, ASCII STLs lacking the SPACE after the 'd' are known to be
  // plentiful.  So, check the first 5 bytes for 'solid'.

  // Several encodings, such as UTF-8, precede the text with up to 5 bytes:
  // https://en.wikipedia.org/wiki/Byte_order_mark#Byte_order_marks_by_encoding
  // Search for "solid" to start anywhere after those prefixes.

  // US-ASCII ordinal values for 's', 'o', 'l', 'i', 'd'
  const solid = [ 115, 111, 108, 105, 100 ];
  for ( let off = 0; off < 5; off ++ ) {
    // If "solid" text is matched to the current offset, declare it to be an ASCII STL.
    if ( matchDataViewAt( solid, view, off ) ) return false;
  }

  // Couldn't find "solid" text at the beginning; it is binary STL.
  return true;
}

